import type { ChangeEventHandler } from 'react'
import { useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import TextButton from 'LuxKit/Button/TextButton'
import RadioInput from 'LuxKit/Radio/RadioInput'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import DropdownChevron from 'LuxKit/Dropdown/DropdownChevron'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import Group from 'components/utils/Group'
import useToggle from 'hooks/useToggle'

export interface DrawerSortOption<T extends string> {
  label: string
  value: T
}

const SortOptionsWrapper = styled.div`
  position: relative;
`

const RadioListItemsGroup = styled(Group)`
  padding-inline: ${rem(16)};
`

interface Props<T extends string> {
  sortValue: T
  sortOptions: Array<DrawerSortOption<T>>
  onSortChange: (sort: T) => void
}

function DrawerSortControl<T extends string>({
  sortValue,
  sortOptions,
  onSortChange,
}: Props<T>) {
  const intl = useIntl()
  const { value: isOpen, toggle, off: close } = useToggle()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const currentOption = sortOptions.find(option => option.value === sortValue) ?? sortOptions[0]

  const sortWithOptionLabel = intl.formatMessage({
    id: 'KQItyfXIPAEQgjABstqE',
    defaultMessage: 'Sort: {option}',
    description: 'Desktop sort button showing current sort option on the form',
  }, { option: currentOption?.label ?? '' })

  const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>((event) => {
    onSortChange(event.currentTarget.value as T)
    close()
  }, [close, onSortChange])

  return (
    <SortOptionsWrapper>
      <TextButton
        kind="secondary"
        variant="dark"
        onClick={toggle}
        endIcon={isOpen ? <LineAngleUpIcon /> : <DropdownChevron />}
        ref={triggerRef}
      >
        {sortWithOptionLabel}
      </TextButton>
      <DropdownList size="S" open={isOpen} onClose={close} anchorRef={triggerRef} triggerRef={triggerRef}>
        <RadioListItemsGroup direction="vertical" gap={16}>
          {sortOptions.map(option => (
            <RadioInput
              key={option.value}
              checked={sortValue === option.value}
              onChange={handleChange}
              name="drawer-sort"
              value={option.value}
            >
              {option.label}
            </RadioInput>
          ))}
        </RadioListItemsGroup>
      </DropdownList>
    </SortOptionsWrapper>
  )
}

export default DrawerSortControl
